import { analyticsAllowedUrl, analyticsPageContext, analyticsReferrer } from './analyticsContext.ts'

export type AnalyticsConsent = 'granted' | 'denied' | 'unset'
export type ReaderAnalyticsEvent = 'homepage_trending_view' | 'trending_tab_change' | 'story_impression' | 'story_open' | 'source_open' | 'pulse_toggle' | 'save_toggle' | 'share_success' | 'discussion_open' | 'wrong_category_report' | 'brief_open' | 'brief_complete' | 'ask_omikina_open' | 'topic_select' | 'mobile_nav_select' | 'caught_up_seen'

const CONSENT_KEY = 'omikina:analytics-consent'
export const ANALYTICS_CONSENT_EVENT = 'omikina:analytics-consent-changed'
const MEASUREMENT_ID = (import.meta.env?.VITE_GA4_MEASUREMENT_ID || 'G-H1HJB6D532').trim()
let memoryConsent: AnalyticsConsent = 'unset'
let previousPage = ''
let currentReferrer = ''

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
    __omikinaLastPageView?: string
    [key: `ga-disable-${string}`]: boolean | undefined
  }
}

export function getAnalyticsConsent(): AnalyticsConsent {
  try {
    const value = window.localStorage.getItem(CONSENT_KEY)
    return value === 'granted' || value === 'denied' ? value : memoryConsent
  } catch { return memoryConsent }
}

export function setAnalyticsConsent(consent: Exclude<AnalyticsConsent, 'unset'>) {
  memoryConsent = consent
  try { window.localStorage.setItem(CONSENT_KEY, consent) } catch { /* Session-only choice when storage is unavailable. */ }
  window[`ga-disable-${MEASUREMENT_ID}`] = consent !== 'granted' || !analyticsAllowedUrl(window.location.href)
  if (consent === 'granted') {
    window.gtag?.('consent', 'update', { analytics_storage: 'granted', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' })
    initializeAnalytics()
    trackPageView(window.location.pathname + window.location.search, document.title)
    trackEvent('analytics_consent_update', { status: 'granted' })
  }
  else {
    window.__omikinaLastPageView = undefined
    previousPage = ''
    currentReferrer = ''
    window.gtag?.('consent', 'update', { analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' })
  }
  window.dispatchEvent(new CustomEvent(ANALYTICS_CONSENT_EVENT, { detail: consent }))
}

export function initializeAnalytics() {
  if (getAnalyticsConsent() !== 'granted' || !analyticsAllowedUrl(window.location.href) || !/^G-[A-Z0-9]+$/.test(MEASUREMENT_ID) || document.querySelector(`script[data-omikina-analytics="${MEASUREMENT_ID}"]`)) return
  window.dataLayer = window.dataLayer || []
  // Google tag commands must be queued as the function's Arguments object.
  // A rest-parameter array looks equivalent but is not processed by gtag.js.
  // eslint-disable-next-line prefer-rest-params -- gtag requires its documented Arguments queue format.
  window.gtag = function () { window.dataLayer?.push(arguments) }
  window.gtag('consent', 'default', { analytics_storage: 'granted', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' })
  window.gtag('js', new Date())
  window.gtag('config', MEASUREMENT_ID, { ...analyticsPageContext(window.location.href), page_referrer: analyticsReferrer(document.referrer), allow_google_signals: false, allow_ad_personalization_signals: false, send_page_view: false })
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`
  script.dataset.omikinaAnalytics = MEASUREMENT_ID
  document.head.appendChild(script)
  trackPageView(window.location.pathname + window.location.search, document.title)
}

export function trackEvent(name: string, parameters: Record<string, unknown> = {}): boolean {
  if (getAnalyticsConsent() !== 'granted' || !window.gtag || !analyticsAllowedUrl(window.location.href)) return false
  window.gtag('event', name, { ...analyticsPageContext(window.location.href), page_title: document.title, page_referrer: currentReferrer, ...parameters })
  return true
}

export function trackReaderEvent(name: ReaderAnalyticsEvent, parameters: Record<string, string | number | boolean> = {}) {
  trackEvent(name, parameters)
}

export function trackPageView(pagePath: string, pageTitle: string) {
  const allowed = getAnalyticsConsent() === 'granted' && analyticsAllowedUrl(window.location.href)
  window[`ga-disable-${MEASUREMENT_ID}`] = !allowed
  if (!allowed) return
  if (!window.gtag) initializeAnalytics()
  if (!window.gtag) return
  const context = analyticsPageContext(pagePath)
  const viewKey = context.page_path
  if (window.__omikinaLastPageView !== viewKey) currentReferrer = previousPage || analyticsReferrer(document.referrer)
  // Metadata may resolve again after article data arrives; update the context
  // without inventing another view of the same screen.
  window.gtag('config', MEASUREMENT_ID, { ...context, page_title: pageTitle, page_referrer: currentReferrer, send_page_view: false })
  if (window.__omikinaLastPageView === viewKey) return
  window.__omikinaLastPageView = viewKey
  window.gtag('event', 'page_view', {
    ...context,
    page_referrer: currentReferrer,
    page_title: pageTitle,
  })
  previousPage = context.canonical_url
}

function analyticsPlacement(element: Element | null): string {
  const section = element?.closest<HTMLElement>('[data-analytics-section]')?.dataset.analyticsSection
  if (section) return section
  if (element?.closest('.global-header')) return 'header'
  if (element?.closest('.reader-rail')) return 'reader_rail'
  if (element?.closest('.global-footer')) return 'footer'
  return 'content'
}

export function beginPageEngagementTracking(path: string): () => void {
  const depths = new Set<number>()
  const sections = new Set<string>()
  let activeMilliseconds = 0
  let lastTick = Date.now()
  let engagementSent = false

  const recordDepth = () => {
    const available = Math.max(1, document.documentElement.scrollHeight - window.innerHeight)
    const percent = Math.min(100, Math.round((window.scrollY / available) * 100))
    for (const threshold of [25, 50, 75, 90]) {
      if (percent >= threshold && !depths.has(threshold) && trackEvent('scroll_depth', { percent_scrolled: threshold, page_path: path })) depths.add(threshold)
    }
  }

  const recordEngagement = () => {
    const now = Date.now()
    if (document.visibilityState === 'visible' && getAnalyticsConsent() === 'granted') activeMilliseconds += now - lastTick
    lastTick = now
    if (engagementSent || document.visibilityState !== 'visible') return
    const seconds = Math.floor(activeMilliseconds / 1000)
    if (seconds >= 30 && trackEvent('reader_engaged', { engagement_seconds: seconds, page_path: path })) engagementSent = true
  }
  const resetVisibilityClock = () => { lastTick = Date.now() }

  const recordLink = (event: MouseEvent) => {
    const anchor = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[href]')
    if (!anchor) return
    const href = anchor.getAttribute('href') || ''
    const placement = analyticsPlacement(anchor)
    const linkLabel = (anchor.getAttribute('aria-label') || anchor.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 80)
    if (href.startsWith('mailto:')) {
      trackEvent('contact_click', { method: 'email', placement, link_label: linkLabel })
      return
    }
    let destination: URL
    try { destination = new URL(href, window.location.origin) } catch { return }
    if (destination.origin === window.location.origin) {
      trackEvent('navigation_click', { destination_path: analyticsPageContext(destination.href).page_path, placement })
    } else {
      trackEvent('outbound_click', { destination_host: destination.hostname, destination_path: destination.pathname.slice(0, 120), placement, link_label: linkLabel })
    }
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return
      const id = (entry.target as HTMLElement).dataset.analyticsSection
      if (id && !sections.has(id) && trackEvent('section_view', { section_id: id, page_path: path })) {
        sections.add(id)
        observer?.unobserve(entry.target)
      }
    })
  }, { threshold: .35 }) : undefined

  document.querySelectorAll<HTMLElement>('[data-analytics-section]').forEach((element) => observer?.observe(element))
  window.addEventListener('scroll', recordDepth, { passive: true })
  document.addEventListener('click', recordLink, true)
  const engagementTimer = window.setInterval(recordEngagement, 10_000)
  document.addEventListener('visibilitychange', resetVisibilityClock)
  const refreshAfterConsent = () => { lastTick = Date.now(); activeMilliseconds = 0; recordDepth() }
  window.addEventListener(ANALYTICS_CONSENT_EVENT, refreshAfterConsent)
  recordDepth()

  return () => {
    window.removeEventListener('scroll', recordDepth)
    document.removeEventListener('click', recordLink, true)
    window.removeEventListener(ANALYTICS_CONSENT_EVENT, refreshAfterConsent)
    window.clearInterval(engagementTimer)
    document.removeEventListener('visibilitychange', resetVisibilityClock)
    observer?.disconnect()
  }
}

let performanceMonitoringStarted = false
export function initializePerformanceMonitoring() {
  if (performanceMonitoringStarted || !analyticsAllowedUrl(window.location.href)) return
  performanceMonitoringStarted = true
  // Core Web Vitals describe the document lifecycle, not the final SPA screen.
  const landing = { ...analyticsPageContext(window.location.href), page_title: document.title, page_referrer: analyticsReferrer(document.referrer) }
  void import('web-vitals').then(({ onCLS, onINP, onLCP }) => {
    const report = (metric: import('web-vitals').Metric) => {
      trackEvent('web_vitals', {
        ...landing,
        metric_name: metric.name,
        metric_value: metric.value,
        metric_delta: metric.delta,
        metric_id: metric.id,
        metric_rating: metric.rating,
        navigation_type: metric.navigationType,
        non_interaction: true,
      })
    }
    onCLS(report)
    onINP(report)
    onLCP(report)
  }).catch(() => { performanceMonitoringStarted = false })
}
